<template>
    <div id="modal-assign-users" ref="modal" class="modal-new invisible">
        <svg class="btn-close-modal" height="24" viewBox="0 0 24 24" width="24"
             xmlns="http://www.w3.org/2000/svg" @click="onCancel"
        >
            <path
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
            />
            <path d="M0 0h24v24H0z" fill="none" />
        </svg>
        <span class="title">Manage Users and Guests</span>
        <div class="modal-body-container">
            <v-tabs background-color="#181818" dark height="40" slider-color="var(--primary)" slider-size="3">
                <v-tab class="pl-0 pr-0 mr-15 text-capitalize color-gray">Users</v-tab>
                <v-tab class="pl-0 pr-0 mr-15 text-capitalize color-gray" :disabled="!canInviteUsers">Add Users</v-tab>
                <v-tab class="pl-0 pr-0 mr-15 text-capitalize color-gray">Guests</v-tab>
                <v-tab class="pl-0 pr-0 mr-15 text-capitalize color-gray" :disabled="!canInviteGuests">Add Guests</v-tab>
                <v-tab class="pl-0 pr-0 text-capitalize color-gray" v-if="canUseGroups">Groups</v-tab>

                <v-tab-item class="pt-5">
                    <span class="text-right d-block mb-2 font-weight-bold">Assigned to this project</span>
                    <div v-for="member in acceptedUsers" :key="member.id" class="user-container">
                        <img :src="getGravatar(member.user.email)" alt="" class="member-avatar" />
                        <div class="member-name">
                            <span>{{ name(member.user) }}</span>
                            <span class="color-warning ml-1" v-if="isUserSuspended(member.user)">(suspended)</span>
                        </div>
                        <span v-if="member.role_id == 2" class="ml-auto" >Always assigned</span>
                        <div v-if="showSwitch(member)" class="switch switch-container">
                            <input :checked="isUserAssigned(member.user.id)" type="checkbox"
                                   @change="onUserStatusChange($event, member.id)"
                            />
                            <span class="slider round"></span>
                        </div>
                    </div>
                </v-tab-item>

                <v-tab-item class="pt-5">
                    <AddUsers :project="project" />
                </v-tab-item>

                <v-tab-item class="pt-5">
                    <span v-if="account.plan_id == 1" class="d-block mt-5 text-center" style="font-size: 12px; line-height: 1.25;">Collaboration is not available on the free plan. <a href="account/change-plan" style="text-decoration: underline; color: inherit;">Upgrade</a> to unlock collaboration features.</span>
                    <span v-else-if="acceptedGuests.length == 0" class="d-block mt-5 text-center" style="font-size: 12px; line-height: 1.25;">You have not invited any guests yet. Go to Add Guests tab to add guests to your organisation. After they accept your invitation they will show up here.</span>

                    <template v-else>
                        <span class="text-right d-block mb-2">Assigned to this project</span>
                        <div v-for="member in acceptedGuests" :key="member.id" class="user-container">
                            <img :src="getGravatar(member.user.email)" alt="" class="member-avatar" />
                            <div class="member-name">
                                <span>{{ name(member.user) }}</span>
                                <span class="color-warning ml-1" v-if="isUserSuspended(member.user)">(suspended)</span>
                            </div>
                            <div v-if="account.id !== member.id && canEdit" class="switch switch-container">
                                <input :checked="isUserAssigned(member.user.id)" type="checkbox"
                                       @change="onUserStatusChange($event, member.id)"
                                />
                                <span class="slider round"></span>
                            </div>
                        </div>
                    </template>
                </v-tab-item>

                <v-tab-item class="pt-5">
                    <AddGuests :project="project" />
                </v-tab-item>

                <v-tab-item class="pt-5">
                    <div class="groups-container">
                        <div v-for="group in groups" :key="group.name" class="user-container">
                            <span class="group-name">{{ group.name }}</span>
                            <v-tooltip :content-class="'members-list-tooltip-container'" :transition="'none'" top>
                                <template v-slot:activator="{ on, attrs }">
                                    <div v-bind="attrs" v-on="on" class="group-avatars-container">
                                        <div
                                            v-for="(account, index) in groupAccounts(group.id)"
                                            :key="index"
                                            :class="{ hide: index >= maxAvatars }"
                                            class="group-avatar-container"
                                        >
                                            <img v-if="index < maxAvatars" :src="getGravatar(account.user.email)"
                                                 class="member-avatar"
                                            />
                                        </div>
                                        <div v-if="groupAccounts(group.id).length > maxAvatars"
                                             class="group-avatar-container --more"
                                        >
                                            <span class="member-avatar">+{{
                                                    groupAccounts(group.id).length - maxAvatars
                                                }}</span>
                                        </div>
                                    </div>
                                </template>
                                <div v-for="account in groupAccounts(group.id)" :key="account.id"
                                     class="user-container"
                                >
                                    <img :src="getGravatar(account.user.email)" alt="" class="member-avatar" />
                                    <span class="member-name">{{ account.user.name }}</span>
                                </div>
                            </v-tooltip>

                            <div v-if="canEdit" class="switch switch-container">
                                <input :checked="isGroupAssigned(group.id)" type="checkbox"
                                       @change="onGroupStatusChange($event, group.name)"
                                />
                                <span class="slider round"></span>
                            </div>
                        </div>
                    </div>
                </v-tab-item>

            </v-tabs>
        </div>

        <div class="buttons-continer">
            <button class="modal-btn --cancel" type="button" @click="onCancel">Close</button>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import AvatarMixins from "@/mixins/Avatar";
import CanMixin from "@/mixins/CanMixin";
import AddUsers from "@/components/account/users/Add";
import AddGuests from "@/components/account/guests/Add";

export default {
    mixins: [AvatarMixins, CanMixin],

    data() {
        return {
            maxAvatars: 4,
        };
    },

    components: {
        AddUsers,
        AddGuests,
    },

    mounted() {
        setInterval(() => {
            if (!this.$refs.modal?.classList.contains('hidden') && this.$route.name == 'app') {
                this.$store.dispatch("Organizations/fetchUsers");
            }
        }, 60000)

        if (this.canAssignUsersAndGroups) {
            this.$store.dispatch("Organizations/fetchUsers");
            this.$store.dispatch("Groups/fetchGroups");
        }
    },

    computed: {
        ...mapGetters("Organizations", ["acceptedUsers", 'acceptedGuests']),
        ...mapState("Groups", ["groups"]),
        ...mapState("User", ["account"]),
        ...mapState("Projects", ["projectSettings"]),

        project() {
            let project = this.$store.getters["Projects/project"](this.projectSettings.id);
            return project ? project : false;
        },
        canEdit() {
            return this.account.role_id <= 4;
        },
    },
    methods: {
        name(user) {
            let name = user.name;
            if (this.$store.state.User.user.id == user.id) name += " (You)";
            return name;
        },

        isUserSuspended(user) {
            if (this.$store.getters['Organizations/accountByUserId'](user.id)?.suspended_at) return true;
            else if (this.$store.getters['Organizations/memberAccountByUserId'](user.id)?.pivot.suspended_at) return true;
        },

        onUserStatusChange(event, id) {
            if (event.target.checked) this.assignUser(id);
            else this.removeUser(id);
        },
        assignUser(id) {
            this.$store
                .dispatch("Projects/assignUsers", {
                    project_id: this.project.id,
                    account_ids: [id],
                })
                .then(() => {
                    this.$store.dispatch("Projects/fetchProjects");
                });
        },
        removeUser(id) {
            this.$store
                .dispatch("Projects/removeUsers", {
                    project_id: this.project.id,
                    account_ids: [id],
                })
                .then(() => {
                    this.$store.dispatch("Projects/fetchProjects");
                });
        },

        isUserAssigned(id) {
            if (!this.project.users) return false;
            return this.project.users.some(user => user.id == id);
        },
        //
        // isUserAssigned(user) {
        //     if (this.selectedProject.users.find(({ id }) => id === user.user.id)) return true;
        //     else return false;
        // },

        onGroupStatusChange(event, name) {
            if (event.target.checked) this.assignGroup(name);
            else this.removeGroup(name);
        },

        assignGroup(name) {
            this.$store
                .dispatch("Projects/assignGroups", {
                    project_id: this.project.id,
                    group_name: [name],
                })
                .then(() => {
                    this.$store.dispatch("Projects/fetchProjects");
                });
        },
        removeGroup(name) {
            this.$store
                .dispatch("Projects/removeGroups", {
                    project_id: this.project.id,
                    group_name: [name],
                })
                .then(() => {
                    this.$store.dispatch("Projects/fetchProjects");
                });
        },
        isGroupAssigned(id) {
            let allUsersAssigned = true;
            this.$store.getters["Groups/getGroup"](id).accounts.forEach(account => {
                if (!this.isUserAssigned(account.id)) allUsersAssigned = false;
            });
            return allUsersAssigned;
        },
        groupAccounts(group_id) {
            // if (group_id == 3)
            // 	return [
            // 		...this.$store.getters["Groups/getGroup"](group_id).accounts,
            // 		this.$store.getters["Groups/getGroup"](group_id).accounts[0],
            // 		// ...this.$store.getters["Groups/getGroup"](group_id).accounts,
            // 	];

            return this.$store.getters["Groups/getGroup"](group_id).accounts;
        },

        onCancel() {
            if (!this.$store.state.UI.UILoadedStatus) {
                document.querySelector("#modal-assign-users").classList.toggle("invisible");
                document.querySelector("#modal-assign-users").classList.toggle("fadeInDownModal");
                document.querySelector("#project-selector-modal").classList.toggle("hidden");
                document.querySelector("#project-selector-modal").classList.toggle("fadeInDownModal");
            } else {
                window.UI.hideModal();
            }
        },

        showSwitch(user) {
            if (this.account.id == user.id) return false;
            if (!this.canEdit) return false;
            if (user.role_id == 2) return false;
            if (user.role_id == 3 && this.account.role_id == 4) return false;

            return true;
        }
    },
};
</script>

<style lang="scss">
#modal-assign-users {
    width: 40%;
    min-height: 50%;

    .modal-body-container {
        max-height: 440px;
        min-height: 150px;
        overflow-y: scroll;
        scrollbar-color: #989898 transparent;
        scrollbar-width: thin;

        &::-webkit-scrollbar {
            width: 5px;
        }

        &::-webkit-scrollbar-thumb {
            background-color: #989898;
            border-radius: 50px;
        }

        .list-title {
            display: block;
            margin: 0 0 10px !important;
            font-size: 12px;
        }

        .groups-container {
            margin-top: 30px;

            .group-avatars-container {
                // flex: 1;
                display: flex;

                .group-avatar-container {
                    margin-left: -5px;
                    position: relative;

                    &.--more {
                        > .member-avatar {
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            font-weight: bold;
                            color: var(--font-default-color);
                            background: #363636;
                        }
                    }

                    img {
                        margin-right: 0;
                    }
                }
            }
        }
    }

    .user-container {
        display: flex;
        // justify-content: space-between;
        align-items: center;
        // min-height: 50px;
        padding: 10px;

        &:hover {
            background: #212121;
            border-radius: 10px;
        }

        .member-avatar {
            border-radius: 50%;
            width: 30px;
            height: 30px;
            border: 1px solid var(--primary);
            background: white;
            margin-right: 10px;
        }

        .group-name {
            margin-right: 20px;
            width: 30%;
        }

        .group-name,
        .member-name {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            color: var(--font-default-color);
            font-size: 12px;
        }
    }

    .buttons-continer {
        margin-top: auto;
    }
}

.members-list-tooltip-container {
    background: #282828;
    border-radius: var(--box-rounding);
    padding: 15px 25px;

    &.menuable__content__active {
        opacity: 1 !important;
    }

    .user-container {
        padding: 10px 0;

        .member-avatar {
            border-radius: 50%;
            width: 30px;
            height: 30px;
            border: 1px solid var(--primary);
            background: white;
            margin-right: 10px;
        }

        .group-name {
            margin-right: 10px;
            width: 30%;
        }

        .group-name,
        .member-name {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            color: var(--font-default-color);
            font-size: 12px;
        }
    }
}

</style>

<style lang="scss" scoped>
::v-deep .v-window__container {
    background: transparent !important;
}

::v-deep .v-list,
.v-select-list {
    background: white !important;
}

::v-deep .v-item-group {
    background: transparent !important;
}

::v-deep .v-card__text {
    padding: 0;
    margin: 0 !important;
    width: 100%;
}

::v-deep .row {
    margin: initial;
}

::v-deep .v-btn {
    //background-color: transparent !important;
}

::v-deep .v-tabs {
    .v-tabs-bar {
        //margin-bottom: 50px;
    }

    .v-tab {
        //min-width: 60px;
        font-size: 12px;
        font-weight: bold;
        color: #989898 !important;
        transition: none;

        &::before {
            display: none;
        }
    }

    .v-tabs-items {
        background: transparent;
        overflow: visible !important;
        padding-top: 15px;
        transition: none;
    }
}
</style>
